import React, { useCallback, useId, useRef, useState } from 'react'
import { SecretField } from '../secret/secret-field'
import { SecretDialog, SecretSection } from '../secret/secret-dialog'
import { useErrorLine } from '../error/use-error-line'
import { customDayRange, DEFAULT_GRACE, expiresAtFor, reasonProblem } from '../../layout/authorized/org/api-keys/key-words'
import { SegmentedChoice } from '../../layout/authorized/org/api-keys/segmented-choice'
import { revokeToken, rotateToken, switchMethod, turnOn } from './scim-api'
import {
  credentialNoun, expiryRules, expiryText, KEEP_EXPIRY, linkMethod, METHOD_BEARER, METHOD_HINTS, METHOD_LABELS, METHOD_OAUTH, NEVER_TOKEN_WARNING, OAUTH_EXPIRY_NOTE, ORG_INTRO,
  sameExpiry, scimErrorText, TOKEN_EXPIRY
} from './scim-words'
import { canKeepExpiry, expiryOptions, expiryOutcome, GRACE_OPTIONS, rotateSummary, shownSummary, switchSteps, switchSummary, turnOnSummary } from './scim-dialog-words'
import { DialogField, DialogSteps, ScimFormDialog } from './scim-form-dialog'
import '../../layout/authorized/org/api-keys/api-keys.scss'
import './scim-dialogs.scss'

// The writes on a link that ask first, shared by the staff workspace and the
// organization's Single sign-on page (spec R22): turn provisioning on (the
// token's expiry: BOUND_EXPIRY, R6), rotate (the organization's own token;
// a grace for the old one, and the new token's expiry: the current date by
// default, or a new one from the same choices), revoke (an optional
// reason), and the token and base URL shown once after a turn-on or a
// rotate, with when the token expires.
//
// A rotate's new expiry is content's optional expires_at. A server that
// doesn't take it yet answers the token with its old expiry: the shown
// token then says the date was kept, never that it changed.
//
// staff: the link is another organization's (content's ?organization_id=);
// the token then shows as made by EducationXR support, and staff never
// rotate (content answers 400: revoke and turn on again).
//
// The secret is held here only from the answer until "I've stored it": then
// this state lets go of it. Nothing else keeps it (no storage, no row).
//
// The look (owner 2026-10-29: "nicer styling structure/alignment"): each
// dialog is the reveal-once dialog's frame (scim-form-dialog.js) -- a short
// title with the connection under it, one or two plain sentences, labelled
// segmented choices (the old credential's grace: 1 hour / 24 hours / 7 days
// / Stop now; the expiry, with a date field for a picked date and the
// warning on Never), for OAuth the steps of what happens, and a live summary
// in the viewer's zone above Cancel and the action. The values sent are
// unchanged (grace_hours 0-168, expires_at, OAuth's 1 to 3 years).
//
// OAuth client credentials (spec R26): turning on offers the method where
// content says OAuth is available (link.oauthAvailable), the Bearer token by
// default; a link on OAuth rotates its client secret; the organization
// switches a live link between the two (a grace for the old credential, and
// the expiry, as a rotation's). An OAuth answer shows the Tenant URL, the
// token endpoint, the client ID and the client secret.

const linkName = link => (link && link.connection && link.connection.name) || 'this connection'
const orgName = link => (link && link.organization && link.organization.name) || 'the organization'
const orgIDOf = (link, staff) => (staff && link && link.organization ? link.organization.id : undefined)


export const useScimActions = ({ staff = false, onChanged }) => {
  const ids = { expiry: useId(), custom: useId(), grace: useId(), reason: useId(), rotateExpiry: useId(), rotateCustom: useId(), method: useId() }
  const [turningOn, setTurningOn] = useState(null)
  const [method, setMethod] = useState(METHOD_BEARER)
  const [switching, setSwitching] = useState(null) // { link, to }
  const [expiry, setExpiry] = useState(TOKEN_EXPIRY.defaultChoice)
  const [customDay, setCustomDay] = useState('')
  const [rotating, setRotating] = useState(null)
  const [grace, setGrace] = useState(DEFAULT_GRACE)
  const [rotateExpiry, setRotateExpiry] = useState(KEEP_EXPIRY)
  const [rotateDay, setRotateDay] = useState('')
  const [revoking, setRevoking] = useState(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [revealed, setRevealed] = useState(null)

  const askTurnOn = useCallback(link => {
    setError(null)
    setExpiry(TOKEN_EXPIRY.defaultChoice)
    setCustomDay('')
    setMethod(METHOD_BEARER)
    setTurningOn(link)
  }, [setError])

  // Switch a live link to the other method (the grace and the expiry are a
  // rotation's).
  const askSwitch = useCallback(link => {
    setError(null)
    const to = linkMethod(link) === METHOD_OAUTH ? METHOD_BEARER : METHOD_OAUTH
    setGrace(DEFAULT_GRACE)
    // A new OAuth client always gets a date (no "keep"): a year by default.
    setRotateExpiry(to === METHOD_OAUTH ? expiryRules(METHOD_OAUTH).defaultChoice : KEEP_EXPIRY)
    setRotateDay('')
    setSwitching({ link, to })
  }, [setError])

  const askRotate = useCallback(link => {
    setError(null)
    setGrace(DEFAULT_GRACE)
    // An OAuth secret that never expires can't be kept: a year by default.
    setRotateExpiry(linkMethod(link) === METHOD_OAUTH && !(link.token && link.token.expiresAt) ? expiryRules(METHOD_OAUTH).defaultChoice : KEEP_EXPIRY)
    setRotateDay('')
    setRotating(link)
  }, [setError])

  const askRevoke = useCallback(link => {
    setError(null)
    setReason('')
    setRevoking(link)
  }, [setError])

  const changed = () => { if (onChanged) onChanged() }

  // What a one-time answer reveals: the Bearer token, or OAuth's values.
  const revealOf = (link, answer, extra) => ({
    link,
    method: answer && answer.authMethod === METHOD_OAUTH ? METHOD_OAUTH : METHOD_BEARER,
    secret: answer && (answer.authMethod === METHOD_OAUTH ? answer.clientSecret : answer.secret),
    clientId: answer && answer.clientId,
    tokenEndpoint: answer && answer.tokenEndpoint,
    baseURL: (answer && answer.baseURL) || link.baseURL,
    token: answer && answer.token,
    ...extra
  })

  // The expiry rules of the credential each dialog makes: an OAuth client
  // secret is 1 to 3 years, a Bearer token keeps the bound-key rules.
  const turnOnRules = expiryRules(turningOn && turningOn.oauthAvailable ? method : METHOD_BEARER)
  const rotateRules = expiryRules(rotating ? linkMethod(rotating) : null)
  const switchRules = expiryRules(switching ? switching.to : null)

  const doTurnOn = async () => {
    if (busy || !turningOn) return
    const expires = expiresAtFor(expiry, customDay, Date.now(), turnOnRules)
    if (expires.error) return setError(expires.error.replace(/\bkey\b/g, 'token'))
    setBusy(true)
    setError(null)
    try {
      const oauth = method === METHOD_OAUTH && turningOn.oauthAvailable
      const answer = oauth
        ? await turnOn(turningOn.connection.id, expires.value, orgIDOf(turningOn, staff), METHOD_OAUTH)
        : await turnOn(turningOn.connection.id, expires.value, orgIDOf(turningOn, staff))
      setBusy(false)
      setRevealed(revealOf(turningOn, answer, { rotated: false }))
      setTurningOn(null)
      changed()
    } catch (err) {
      setBusy(false)
      setError(scimErrorText(err, "Provisioning couldn't be turned on."), err)
    }
  }

  const doRotate = async () => {
    if (busy || !rotating) return
    let expiresAt
    if (rotateExpiry !== KEEP_EXPIRY) {
      const expires = expiresAtFor(rotateExpiry, rotateDay, Date.now(), rotateRules)
      if (expires.error) return setError(expires.error.replace(/\bkey\b/g, 'token'))
      expiresAt = expires.value
    }
    setBusy(true)
    setError(null)
    try {
      const answer = await rotateToken(rotating.connection.id, grace, expiresAt)
      const previous = rotating.token ? rotating.token.expiresAt : null
      const answered = answer && answer.token
      // A new date asked for, and the token came back with the old one: the
      // server kept it (content without the optional expires_at yet).
      const kept = expiresAt !== undefined && !!answered && !sameExpiry(expiresAt, previous) && sameExpiry(answered.expiresAt, previous)
      setBusy(false)
      setRevealed(revealOf(rotating, answer, { rotated: true, graceHours: grace, graceNoun: credentialNoun(rotating), token: answered, expiryKept: kept }))
      setRotating(null)
      changed()
    } catch (err) {
      setBusy(false)
      setError(scimErrorText(err, "The token couldn't be rotated."), err)
    }
  }

  const doSwitch = async () => {
    if (busy || !switching) return
    let expiresAt
    if (rotateExpiry !== KEEP_EXPIRY) {
      const expires = expiresAtFor(rotateExpiry, rotateDay, Date.now(), switchRules)
      if (expires.error) return setError(expires.error.replace(/\bkey\b/g, 'token'))
      expiresAt = expires.value
    }
    setBusy(true)
    setError(null)
    try {
      const answer = await switchMethod(switching.link.connection.id, switching.to, grace, expiresAt)
      setBusy(false)
      setRevealed(revealOf(switching.link, answer, { rotated: true, graceHours: grace, graceNoun: credentialNoun(switching.link) }))
      setSwitching(null)
      changed()
    } catch (err) {
      setBusy(false)
      setError(scimErrorText(err, "The authentication method couldn't be switched."), err)
    }
  }

  const doRevoke = async () => {
    if (busy || !revoking) return
    const problem = reasonProblem(reason)
    if (problem) return setError(problem)
    setBusy(true)
    setError(null)
    try {
      await revokeToken(revoking.connection.id, reason, orgIDOf(revoking, staff))
      setBusy(false)
      setRevoking(null)
      changed()
    } catch (err) {
      setBusy(false)
      setError(scimErrorText(err, "The token couldn't be revoked."), err)
    }
  }

  const now = Date.now()
  const oauthTurnOn = Boolean(turningOn && turningOn.oauthAvailable && method === METHOD_OAUTH)
  const turnOnOutcome = turningOn ? expiryOutcome({ choice: expiry, customDay, rules: turnOnRules, now }) : null
  const rotateNoun = rotating ? credentialNoun(rotating) : 'token'
  const rotateOutcome = rotating ? expiryOutcome({ choice: rotateExpiry, customDay: rotateDay, rules: rotateRules, token: rotating.token, now }) : null
  const switchOutcome = switching ? expiryOutcome({ choice: rotateExpiry, customDay: rotateDay, rules: switchRules, token: switching.link.token, now }) : null
  const where = link => (staff ? `${orgName(link)} · ${linkName(link)}` : linkName(link))

  const dialogs = (
    <>
      <ScimFormDialog
        open={Boolean(turningOn)}
        testId="scim-turn-on-dialog"
        title="Turn on provisioning"
        subtitle={turningOn ? where(turningOn) : ''}
        intro={turningOn
          ? staff
            ? `EducationXR makes a ${oauthTurnOn ? 'client ID and secret' : 'token'} for the identity provider of ${orgName(turningOn)} and shows it once, with the Tenant URL. People that identity provider provisions are added and removed there. The organization sees it as made by EducationXR support.`
            : `EducationXR makes a ${oauthTurnOn ? 'client ID and secret' : 'token'} for your identity provider and shows it once, with the Tenant URL. ${ORG_INTRO}`
          : ''}
        summary={turnOnSummary({ method: oauthTurnOn ? METHOD_OAUTH : METHOD_BEARER, outcome: turnOnOutcome })}
        confirmText="Turn on provisioning"
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={doTurnOn}
        onCancel={() => setTurningOn(null)}>
        {turningOn && turningOn.oauthAvailable &&
          <DialogField id={ids.method} label="How your identity provider signs in" hint={METHOD_HINTS[method]}>
            <SegmentedChoice name={`${ids.method}-method`} labelledBy={ids.method} className="ak-seg-fill"
              options={[METHOD_BEARER, METHOD_OAUTH].map(m => ({ value: m, label: METHOD_LABELS[m] }))} value={method}
              onChange={m => { setMethod(m); setExpiry(expiryRules(m).defaultChoice); setCustomDay('') }} />
          </DialogField>}
        {oauthTurnOn &&
          <DialogSteps steps={[
            'EducationXR creates a client ID and secret, shown once.',
            'You paste them, with the Tenant URL and the token endpoint, into your identity provider.',
            'Your identity provider exchanges them for an access token that lasts an hour, on its own.'
          ]} />}
        <ExpiryField id={ids.expiry} label={oauthTurnOn ? 'The client secret expires' : 'The token expires'} rules={turnOnRules}
          options={expiryOptions(turnOnRules)} value={expiry} onChange={setExpiry} day={customDay} onDay={setCustomDay} dayId={ids.custom}
          note={oauthTurnOn ? OAUTH_EXPIRY_NOTE : null} />
      </ScimFormDialog>

      <ScimFormDialog
        open={Boolean(rotating)}
        testId="scim-rotate-dialog"
        title={rotating && linkMethod(rotating) === METHOD_OAUTH ? 'Rotate client secret' : 'Rotate token'}
        subtitle={rotating ? where(rotating) : ''}
        intro={rotating
          ? `A new ${rotateNoun} replaces this one and is shown once. Keep the old one working long enough to paste the new one into your identity provider.${linkMethod(rotating) === METHOD_OAUTH ? " The old secret's access tokens stop when it does." : ''}`
          : ''}
        summary={rotating ? rotateSummary({ noun: rotateNoun, graceHours: grace, outcome: rotateOutcome, now }) : ''}
        confirmText={rotating && linkMethod(rotating) === METHOD_OAUTH ? 'Rotate client secret' : 'Rotate token'}
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={doRotate}
        onCancel={() => setRotating(null)}>
        <DialogField id={ids.grace} label={`The old ${rotateNoun} keeps working`}>
          <SegmentedChoice name={`${ids.grace}-grace`} labelledBy={ids.grace} className="ak-seg-fill"
            options={GRACE_OPTIONS} value={String(grace)} onChange={v => setGrace(Number(v))} />
        </DialogField>
        <ExpiryField id={ids.rotateExpiry} label={`The new ${rotateNoun} expires`} rules={rotateRules}
          options={expiryOptions(rotateRules, { keep: canKeepExpiry(rotateRules, rotating && rotating.token), token: rotating && rotating.token })}
          value={rotateExpiry} onChange={setRotateExpiry} day={rotateDay} onDay={setRotateDay} dayId={ids.rotateCustom} />
      </ScimFormDialog>

      <ScimFormDialog
        open={Boolean(switching)}
        testId="scim-switch-dialog"
        title={switching && switching.to === METHOD_OAUTH ? 'Switch to OAuth' : 'Switch to a Bearer token'}
        subtitle={switching ? where(switching.link) : ''}
        intro={switching
          ? switching.to === METHOD_OAUTH
            ? 'Your identity provider signs in with a client ID and secret instead of the Bearer token, and exchanges them for a token that lasts an hour.'
            : 'Your identity provider signs in with one long-lived Bearer token instead of the client ID and secret.'
          : ''}
        summary={switching ? switchSummary({ to: switching.to, graceHours: grace, outcome: switchOutcome, now }) : ''}
        confirmText={switching && switching.to === METHOD_OAUTH ? 'Switch to OAuth' : 'Switch to a Bearer token'}
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={doSwitch}
        onCancel={() => setSwitching(null)}>
        {switching && <DialogSteps steps={switchSteps({ to: switching.to, graceHours: grace, now })} />}
        <DialogField id={`${ids.grace}-switch`} label={switching && switching.to === METHOD_OAUTH ? 'The Bearer token keeps working' : 'The client secret keeps working'}>
          <SegmentedChoice name={`${ids.grace}-switch-grace`} labelledBy={`${ids.grace}-switch`} className="ak-seg-fill"
            options={GRACE_OPTIONS} value={String(grace)} onChange={v => setGrace(Number(v))} />
        </DialogField>
        <ExpiryField id={`${ids.rotateExpiry}-switch`} label="The new credentials expire" rules={switchRules}
          options={expiryOptions(switchRules, { keep: switchRules === TOKEN_EXPIRY, token: switching && switching.link.token })}
          value={rotateExpiry} onChange={setRotateExpiry} day={rotateDay} onDay={setRotateDay} dayId={`${ids.rotateCustom}-switch`}
          note={switching && switching.to === METHOD_OAUTH ? OAUTH_EXPIRY_NOTE : null} />
      </ScimFormDialog>

      <ScimFormDialog
        open={Boolean(revoking)}
        testId="scim-revoke-dialog"
        title={revoking && linkMethod(revoking) === METHOD_OAUTH ? 'Revoke client credentials' : 'Revoke token'}
        subtitle={revoking ? where(revoking) : ''}
        intro={revoking
          ? `The identity provider's requests stop working at once${linkMethod(revoking) === METHOD_OAUTH ? ': the client secret and every access token stop' : ''}. People it provisioned stay as they are: nobody is removed.`
          : ''}
        summary={revoking ? `To provision again, turn provisioning on and give the identity provider the new ${linkMethod(revoking) === METHOD_OAUTH ? 'credentials' : 'token'}.` : ''}
        confirmText="Revoke token"
        confirmClassName="btn sw-btn-danger"
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={doRevoke}
        onCancel={() => setRevoking(null)}>
        <div className="sd-field">
          <label className="sd-label d-block" htmlFor={ids.reason}>Reason (optional)</label>
          <textarea id={ids.reason} className="form-control" rows={2} maxLength={600} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
      </ScimFormDialog>

      {revealed &&
        <ScimTokenDialog
          connectionName={linkName(revealed.link)}
          organizationName={staff ? orgName(revealed.link) : null}
          method={revealed.method}
          baseURL={revealed.baseURL}
          secret={revealed.secret}
          clientId={revealed.clientId}
          tokenEndpoint={revealed.tokenEndpoint}
          graceHours={revealed.rotated ? revealed.graceHours : null}
          graceNoun={revealed.graceNoun}
          expiresText={tokenExpiresText(revealed.token, revealed.expiryKept)}
          onDone={() => setRevealed(null)} />}
    </>
  )

  return { askTurnOn, askRotate, askSwitch, askRevoke, dialogs }
}

// An expiry choice: the segmented options, a date field when a date is to
// be picked, the warning on Never, and an optional note.
const ExpiryField = ({ id, label, rules, options, value, onChange, day, onDay, dayId, note }) => {
  const range = value === 'custom' ? customDayRange(Date.now(), rules) : null
  return (
    <DialogField id={id} label={label} hint={note}>
      <SegmentedChoice name={`${id}-expiry`} labelledBy={id} className="ak-seg-fill" options={options} value={value} onChange={onChange} />
      {value === 'custom' &&
        <div className="sd-date-row">
          <label className="sw-label" htmlFor={dayId}>Expires on</label>
          <input id={dayId} className="form-control sd-date" type="date" value={day} min={range.min} max={range.max} onChange={e => onDay(e.target.value)} />
        </div>}
      {value === 'never' && <div className="sw-notice warn mt-2" role="status"><div>{NEVER_TOKEN_WARNING}</div></div>}
    </DialogField>
  )
}

// The token and the Tenant URL, shown once (content keeps only a hash).
// Escape and the backdrop don't close it, so it is never lost by a stray
// key or click; "I've stored it" does.
// When the shown token expires, in words (null when the answer didn't say).
export const tokenExpiresText = (token, kept) => {
  if (!token || !('expiresAt' in token)) return null
  if (!token.expiresAt) return 'Never expires'
  return kept ? `Expires on ${expiryText(token)} (unchanged)` : `Expires on ${expiryText(token)}`
}

const IDPS = [
  {
    id: 'entra', label: 'Microsoft Entra ID',
    steps: [
      'Open your provisioning app, then Provisioning, then Connect your application.',
      'Choose Bearer authentication.',
      'Paste the Tenant URL and the Secret token.',
      'Select Test connection, then Create.'
    ],
    oauthSteps: [
      'Open your provisioning app, then Provisioning, then Connect your application.',
      'Authentication method: choose OAuth2 client credentials grant.',
      'Tenant URL: paste the Tenant URL. OAuth token endpoint: paste the Token endpoint.',
      'Client ID and Client secret: paste them. How credentials are sent: Header (Body and Both work too). Leave the OAuth 2.0 scopes empty.',
      'Select Test connection, then Create.'
    ]
  },
  {
    id: 'okta', label: 'Okta',
    steps: [
      'In the SCIM app, set the SCIM connector base URL to the Tenant URL.',
      'Set Authentication mode to HTTP Header.',
      'Paste the Secret token as the Bearer token.'
    ],
    oauthSteps: [
      "Okta's SCIM apps send a Bearer token: switch this connection to a Bearer token for Okta."
    ]
  },
  {
    id: 'other', label: 'Other SCIM client',
    steps: [
      'Use the Tenant URL as the SCIM 2.0 base URL.',
      'Send the Secret token as a Bearer token.'
    ],
    oauthSteps: [
      'Use the Tenant URL as the SCIM 2.0 base URL.',
      'POST grant_type=client_credentials to the Token endpoint, authenticating with the Client ID and Client secret (HTTP Basic, or client_id and client_secret form fields).',
      'Send the access_token as a Bearer token. It lasts an hour (expires_in); ask for a new one when it ends.'
    ]
  }
]

// Setup steps for each identity provider, as tabs (arrow keys move between
// them; the tab list is one Tab stop).
const IdpTabs = ({ method = METHOD_BEARER }) => {
  const [current, setCurrent] = useState(IDPS[0].id)
  const base = useId()
  const refs = useRef({})
  const onKeyDown = e => {
    const i = IDPS.findIndex(t => t.id === current)
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      const next = IDPS[e.key === 'Home' ? 0 : IDPS.length - 1].id
      setCurrent(next)
      if (refs.current[next]) refs.current[next].focus()
    } else if (delta) {
      e.preventDefault()
      const next = IDPS[(i + delta + IDPS.length) % IDPS.length].id
      setCurrent(next)
      if (refs.current[next]) refs.current[next].focus()
    }
  }
  const active = IDPS.find(t => t.id === current)
  return (
    <>
      <div className="ak-tabs" role="tablist" aria-label="Identity provider" onKeyDown={onKeyDown}>
        {IDPS.map(t =>
          <button key={t.id} type="button" role="tab" className="ak-tab" id={`${base}-${t.id}`} aria-selected={t.id === current}
            aria-controls={`${base}-panel`} tabIndex={t.id === current ? 0 : -1} ref={el => { refs.current[t.id] = el }} onClick={() => setCurrent(t.id)}>{t.label}</button>)}
      </div>
      <div role="tabpanel" id={`${base}-panel`} aria-labelledby={`${base}-${current}`}>
        <ol className="ak-steps">{(method === METHOD_OAUTH ? active.oauthSteps : active.steps).map(step => <li key={step}>{step}</li>)}</ol>
      </div>
    </>
  )
}

export const ScimTokenDialog = ({ connectionName, organizationName, method = METHOD_BEARER, baseURL, secret, clientId, tokenEndpoint, graceHours = null, graceNoun = 'token', expiresText, onDone }) => {
  const oauth = method === METHOD_OAUTH
  const summary = shownSummary({ graceHours, graceNoun })
  let n = 0
  const next = () => { n += 1; return n }
  return (
    <SecretDialog title={oauth ? 'Copy the client credentials' : 'Copy the token'}
      subtitle={organizationName ? `${organizationName} · ${connectionName}` : connectionName}
      backdropTestId="scim-token-backdrop" onDone={onDone}
      note={oauth
        ? 'This is the only time the client secret is shown. EducationXR keeps only a hash of it, so it can\'t be shown again.'
        : 'This is the only time the token is shown. EducationXR keeps only a hash of it, so it can\'t be shown again.'}>
      {summary && <p className="sd-summary sd-shown-summary" data-testid="scim-token-summary">{summary}</p>}
      {baseURL &&
        <SecretSection n={next()} title="Tenant URL (SCIM base URL)">
          <SecretField plain value={baseURL} noun="Tenant URL" testId="scim-tenant-url" />
        </SecretSection>}
      {oauth && tokenEndpoint &&
        <SecretSection n={next()} title="Token endpoint">
          <SecretField plain value={tokenEndpoint} noun="token endpoint" testId="scim-token-endpoint" />
        </SecretSection>}
      {oauth && clientId &&
        <SecretSection n={next()} title="Client ID">
          <SecretField plain value={clientId} noun="client ID" testId="scim-client-id" />
        </SecretSection>}
      <SecretSection n={next()} title={oauth ? 'Client secret' : 'Secret token'}>
        <SecretField value={secret} noun={oauth ? 'client secret' : 'token'} testId={oauth ? 'scim-client-secret' : 'scim-token-secret'} />
        {expiresText ? <span className="ak-chip mt-2" data-testid="scim-token-expires">{expiresText}</span> : null}
      </SecretSection>
      <SecretSection n={next()} title="Set it up in your identity provider">
        <IdpTabs method={method} />
      </SecretSection>
    </SecretDialog>
  )
}
